/**
 * QA Panel — translation coverage calculator.
 *
 * Walks i18next's resource store to compute, for each loaded language, how
 * many keys are present vs the reference language (English). Returns a flat
 * list of missing keys per language so the popup can render a coverage matrix.
 *
 * Robustness notes:
 *   - i18next normalizes language codes through formatLanguageCode, which
 *     can produce 'bg', 'BG', or 'bg-BG' depending on options. So we look up
 *     languages case-insensitively and tolerate region suffixes.
 *   - Namespaces aren't always 'translation'. We walk every namespace under
 *     each language and union all the keys, prefixing non-default namespaces
 *     so they don't collide.
 *   - "Loaded" matters here — i18next only has data for languages the user
 *     has actually visited. The bridge can call preloadAllLanguages() to
 *     fetch the rest before computing coverage if QA wants the full picture.
 */

import i18next from '../../../../i18n';
import { REFERENCE_LANGUAGE } from '../constants';
import type { LanguageCoverage } from '../types';

type NestedDict = { [key: string]: unknown };

function isNestedDict(value: unknown): value is NestedDict {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Flatten a nested translation object into dot-separated keys.
 *   { settings: { title: 'Settings' } }  →  ['settings.title']
 * Optional `prefix` is used to namespace keys (e.g. for non-default namespaces).
 */
function flattenKeys(dict: NestedDict, prefix = '', out = new Set<string>()): Set<string> {
    for (const k of Object.keys(dict)) {
        const v = dict[k];
        const path = prefix ? `${prefix}.${k}` : k;
        if (typeof v === 'string') {
            out.add(path);
        } else if (isNestedDict(v)) {
            flattenKeys(v, path, out);
        }
    }
    return out;
}

/**
 * Same as flattenKeys but also captures values. Used by the language
 * inspector to compare each key's value against the reference language.
 */
function flattenKeyValues(
    dict: NestedDict,
    prefix = '',
    out = new Map<string, string>()
): Map<string, string> {
    for (const k of Object.keys(dict)) {
        const v = dict[k];
        const path = prefix ? `${prefix}.${k}` : k;
        if (typeof v === 'string') {
            out.set(path, v);
        } else if (isNestedDict(v)) {
            flattenKeyValues(v, path, out);
        }
    }
    return out;
}

/**
 * Pull a snapshot of the i18next resource store. Uses the documented
 * `services.resourceStore.toJSON()` API rather than poking at the internal
 * `data` field, so we don't break if i18next refactors internals.
 *
 * Returns shape: { [lng: string]: { [namespace: string]: NestedDict } }
 */
function getResourceSnapshot(): Record<string, Record<string, NestedDict>> {
    type Store = { toJSON?: () => Record<string, Record<string, NestedDict>>; data?: Record<string, Record<string, NestedDict>> };
    const services = (i18next as unknown as { services?: { resourceStore?: Store } }).services;
    const store = services?.resourceStore;
    if (!store) return {};
    if (typeof store.toJSON === 'function') {
        try {
            return store.toJSON() ?? {};
        } catch {
            // fall through
        }
    }
    return store.data ?? {};
}

export function getLoadedLanguages(): string[] {
    return Object.keys(getResourceSnapshot());
}

/**
 * Per-key inspection result for one target language.
 *
 * Status meanings:
 *   - missing      : key exists in the reference language but not in the target
 *   - empty        : key exists, value is "" or whitespace-only
 *   - keyAsValue   : key exists, value === key (i18next fell back to the key string)
 *   - sameAsRef    : key exists, value === reference value (likely untranslated copy)
 *   - translated   : key exists, value differs from reference and looks legitimate
 *
 * Note that `sameAsRef` is a *suspect*, not always a bug — proper nouns, brand
 * names, or single-letter abbreviations can legitimately match across languages.
 * The inspector lets QA judge each one.
 */
export type InspectionStatus = 'missing' | 'empty' | 'keyAsValue' | 'sameAsRef' | 'translated';

export interface InspectionItem {
    key: string;
    refValue: string | null;
    langValue: string | null;
    status: InspectionStatus;
}

export interface LanguageInspection {
    lang: string;
    referenceLang: string;
    counts: Record<InspectionStatus, number>;
    items: InspectionItem[];
}

const STATUS_ORDER: Record<InspectionStatus, number> = {
    missing: 0,
    empty: 1,
    keyAsValue: 2,
    sameAsRef: 3,
    translated: 4,
};

/**
 * Classify every key in the reference language for a target language.
 * Returns the items sorted by severity (missing first, translated last)
 * and then alphabetically by key, plus per-status counts for the UI.
 */
export function inspectLanguage(
    targetLang: string,
    referenceLang: string = REFERENCE_LANGUAGE
): LanguageInspection {
    const snapshot = getResourceSnapshot();
    const merged = buildMergedSnapshot(snapshot);
    const refEntry = merged[normalizeLangCode(referenceLang)];
    const targetEntry = merged[normalizeLangCode(targetLang)];

    const counts: Record<InspectionStatus, number> = {
        missing: 0, empty: 0, keyAsValue: 0, sameAsRef: 0, translated: 0,
    };

    if (!refEntry) {
        return { lang: targetLang, referenceLang, counts, items: [] };
    }

    const items: InspectionItem[] = [];

    refEntry.keys.forEach((key) => {
        const refValue = refEntry.values.get(key) ?? null;
        const langValue = targetEntry?.values.get(key);

        let status: InspectionStatus;
        if (langValue === undefined) {
            status = 'missing';
        } else if (langValue === '' || /^\s*$/.test(langValue)) {
            status = 'empty';
        } else if (langValue === key) {
            status = 'keyAsValue';
        } else if (refValue !== null && langValue === refValue) {
            status = 'sameAsRef';
        } else {
            status = 'translated';
        }

        counts[status] += 1;
        items.push({
            key,
            refValue,
            langValue: langValue ?? null,
            status,
        });
    });

    items.sort((a, b) => {
        const d = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        return d !== 0 ? d : a.key.localeCompare(b.key);
    });

    return {
        lang: targetEntry?.display ?? targetLang.toUpperCase(),
        referenceLang: refEntry.display,
        counts,
        items,
    };
}

/**
 * Normalize a language code so case + region variants collapse together.
 *   'EN'    → 'en'
 *   'en-US' → 'en'
 *   'bg-BG' → 'bg'
 *
 * This is the deduplication key — every variant of the same language is
 * treated as one virtual entry whose key set is the union of all variants.
 * Necessary because i18next can store the same language under multiple keys
 * (e.g. 'EN' from explicit addResourceBundle + 'en' from the fallback init).
 */
function normalizeLangCode(lang: string): string {
    const lower = lang.toLowerCase();
    const dash = lower.indexOf('-');
    return dash === -1 ? lower : lower.slice(0, dash);
}

/**
 * Collect every translation key for a single store entry, across all namespaces.
 * Non-default namespaces are prefixed with `${ns}:` to avoid collision.
 */
function collectKeysForLanguage(
    snapshot: Record<string, Record<string, NestedDict>>,
    langKey: string
): Set<string> {
    const out = new Set<string>();
    const namespaces = snapshot[langKey];
    if (!namespaces) return out;
    for (const ns of Object.keys(namespaces)) {
        const dict = namespaces[ns];
        if (!isNestedDict(dict)) continue;
        const prefix = ns === 'translation' ? '' : `${ns}:`;
        flattenKeys(dict, prefix, out);
    }
    return out;
}

/**
 * Like collectKeysForLanguage, but returns key→value pairs.
 */
function collectValuesForLanguage(
    snapshot: Record<string, Record<string, NestedDict>>,
    langKey: string
): Map<string, string> {
    const out = new Map<string, string>();
    const namespaces = snapshot[langKey];
    if (!namespaces) return out;
    for (const ns of Object.keys(namespaces)) {
        const dict = namespaces[ns];
        if (!isNestedDict(dict)) continue;
        const prefix = ns === 'translation' ? '' : `${ns}:`;
        flattenKeyValues(dict, prefix, out);
    }
    return out;
}

interface MergedLanguage {
    /** Normalized code, e.g. 'en'. */
    normalized: string;
    /** Display label — uppercase normalized code so the matrix reads cleanly. */
    display: string;
    /** Union of keys from every variant in the snapshot. */
    keys: Set<string>;
    /** Union of key→value pairs across variants. Later variants overwrite earlier ones. */
    values: Map<string, string>;
    /** Original snapshot keys that were merged into this entry. */
    sourceKeys: string[];
}

/**
 * Build a deduplicated view of the resource store, keyed by normalized
 * language code. Variants like 'EN' and 'en' get merged into a single
 * entry whose key set is the union of both bundles.
 */
function buildMergedSnapshot(
    snapshot: Record<string, Record<string, NestedDict>>
): Record<string, MergedLanguage> {
    const merged: Record<string, MergedLanguage> = {};
    for (const langKey of Object.keys(snapshot)) {
        const normalized = normalizeLangCode(langKey);
        let entry = merged[normalized];
        if (!entry) {
            entry = {
                normalized,
                display: normalized.toUpperCase(),
                keys: new Set<string>(),
                values: new Map<string, string>(),
                sourceKeys: [],
            };
            merged[normalized] = entry;
        }
        entry.sourceKeys.push(langKey);
        const values = collectValuesForLanguage(snapshot, langKey);
        values.forEach((v, k) => {
            entry.keys.add(k);
            entry.values.set(k, v);
        });
    }
    return merged;
}

/**
 * Compute coverage for every loaded language vs the reference language.
 *
 * Variants of the same language (case, region suffix) are merged before
 * comparison. If the reference language isn't loaded at all, falls back to
 * per-language self-counts (so the matrix still shows *something*).
 */
export function computeCoverage(
    referenceLang: string = REFERENCE_LANGUAGE
): Record<string, LanguageCoverage> {
    const snapshot = getResourceSnapshot();
    if (Object.keys(snapshot).length === 0) return {};

    const merged = buildMergedSnapshot(snapshot);
    const refNormalized = normalizeLangCode(referenceLang);
    const referenceEntry = merged[refNormalized];
    const referenceKeys = referenceEntry ? referenceEntry.keys : null;

    const result: Record<string, LanguageCoverage> = {};

    for (const normalized of Object.keys(merged)) {
        const entry = merged[normalized];

        if (!referenceKeys || referenceKeys.size === 0) {
            // No reference: report self-counts only.
            result[entry.display] = {
                lang: entry.display,
                total: entry.keys.size,
                present: entry.keys.size,
                missingKeys: [],
            };
            continue;
        }

        const missing: string[] = [];
        referenceKeys.forEach((key) => {
            if (!entry.keys.has(key)) missing.push(key);
        });
        result[entry.display] = {
            lang: entry.display,
            total: referenceKeys.size,
            present: referenceKeys.size - missing.length,
            missingKeys: missing,
        };
    }

    return result;
}
